/**
 * Helcim Pay `start.js` adds a global embed (element id `helcimPayIframe`). Teardown must be explicit:
 * their remove helper sometimes no-ops; we always remove the node by id so Radix/dialog flows don’t leave a blocker.
 */
export const HELCIM_PAY_IFRAME_ID = 'helcimPayIframe';

declare global {
  interface Window {
    removeHelcimPayIframe?: () => void;
  }
}

/** Call on SUCCESS, ABORTED, HIDE, dialog close, and in-app Cancel while the iframe may be open. */
export function closeHelcimPayIframe(): void {
  try {
    window.removeHelcimPayIframe?.();
  } catch {
    /* ignore */
  }
  document.getElementById(HELCIM_PAY_IFRAME_ID)?.remove();
}

/**
 * Dialog overlay/content use Tailwind `z-50`. Helcim’s embed may render lower — then clicks hit the overlay.
 * Call immediately after `appendHelcimPayIframe`; a microtask + next frame catches late DOM writes.
 */
export function ensureHelcimPayIframeStacking(): void {
  const apply = () => {
    const el = document.getElementById(HELCIM_PAY_IFRAME_ID) as HTMLElement | null;
    if (!el) return;
    el.style.setProperty('z-index', '100000', 'important');
    el.style.setProperty('pointer-events', 'auto', 'important');
    if (window.getComputedStyle(el).position === 'static') {
      el.style.setProperty('position', 'fixed', 'important');
    }
  };
  apply();
  queueMicrotask(apply);
  requestAnimationFrame(apply);
}

const DEFAULT_EMBED_POLL_MS = 48;
const DEFAULT_EMBED_MAX_WAIT_MS = 14_000;
const DEFAULT_EMBED_MIN_SIZE = 2;

/**
 * After `appendHelcimPayIframe`, Helcim may insert `#helcimPayIframe` before inner captcha/checkout
 * has laid out. Poll until the node has non-trivial size or `maxMs` elapses (then return false).
 */
export async function waitForHelcimPayEmbedPainted(options?: {
  maxMs?: number;
  pollMs?: number;
  minWidth?: number;
  minHeight?: number;
}): Promise<boolean> {
  const maxMs = options?.maxMs ?? DEFAULT_EMBED_MAX_WAIT_MS;
  const pollMs = options?.pollMs ?? DEFAULT_EMBED_POLL_MS;
  const minW = options?.minWidth ?? DEFAULT_EMBED_MIN_SIZE;
  const minH = options?.minHeight ?? DEFAULT_EMBED_MIN_SIZE;
  const deadline = performance.now() + maxMs;

  while (performance.now() < deadline) {
    const el = document.getElementById(HELCIM_PAY_IFRAME_ID) as HTMLElement | null;
    if (el) {
      const { width, height } = el.getBoundingClientRect();
      if (width >= minW && height >= minH) {
        ensureHelcimPayIframeStacking();
        return true;
      }
    }
    await new Promise((r) => setTimeout(r, pollMs));
  }
  return false;
}
